import { Component, OnInit, Inject, ChangeDetectionStrategy } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AuthService } from '@core/services/auth.service';
import { StudentRequestService } from '@core/services/student-request.service';
import { ClassService } from '@core/services/class.service';
import { take } from 'rxjs';
import { IRatingPeriod } from '@core/interfaces/iratingperiod';
import { StudentRequestReason } from '@core/enums/student-request-reason';
import { IClass } from '@core/interfaces/iclasses';
import { PermissionService } from '@core/services/permission.service';
import { Permission } from '@core/enums/permissions';
import { RatingViewVersionEnum } from '@core/enums/rating-view-version.enum';
import { ToastService } from '@core/services/toast.service';
import { StateService } from '@core/services/state.service';
import { TranslocoService } from '@jsverse/transloco';

@Component({
    selector: 'drdp-re-enroll-classroom',
    templateUrl: './re-enroll-classroom.component.html',
    styleUrls: ['./re-enroll-classroom.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ReEnrollClassroomComponent implements OnInit {
  user = this.authService.getCurrentUser();
  ratingOptions: IRatingPeriod[] = [];
  submitBtnText = '';
  isAdmin: boolean = false;
  reEnrollmentForm: FormGroup | any;
  existingClasses: IClass[] = [];
  displayResult = false;
  isProcessing = false;
  requireAgeGrade: boolean = false;
  selectedSchoolYearId: number | null = null;
  is2025StateConfig = false;
  isUserState2025Enforced = false;

  get ratingPeriodId() {
    return this.reEnrollmentForm.get('ratingPeriodId');
  }

  get ageGradeId() {
    return this.reEnrollmentForm.get('ageGradeId');
  }

  constructor(
    private toastService: ToastService,
    private authService: AuthService,
    private permissionService: PermissionService,
    private studentRequestService: StudentRequestService,
    private classService: ClassService,
    private fb: FormBuilder,
    private stateService: StateService,
    public dialogRef: MatDialogRef<ReEnrollClassroomComponent>,
    private translocoService: TranslocoService,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    dialogRef.disableClose = true;
  }

  ngOnInit(): void {
    this.initializeForms();

    this.stateService.isUserState2025Enforced().pipe(take(1)).subscribe((enforced) => (this.isUserState2025Enforced = enforced));

    this.isAdmin = this.permissionService.checkPermission(
      Permission.AdminEnrollmentByPass
    );

    this.submitBtnText = this.isAdmin
      ? this.translocoService.translate(
          'user.manageEnrollment.reEnrollClassroom.saveAndReEnroll'
        )
      : this.translocoService.translate(
          'user.manageEnrollment.reEnrollClassroom.saveAndSendForApproval'
        );

    this.classService
      .getClassesByClassroom(this.data.classroomId)
      .pipe(take(1))
      .subscribe((result) => {
        const ratingPeriods = result.flatMap((obj) => obj.ratingPeriod);

        const currentRatingPeriod = ratingPeriods.find(
          (r: any) => r.id == this.data.ratingPeriodId
        );

        if (currentRatingPeriod) {
          const currentDate = new Date();
          const dateWithoutTime = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());
          this.ratingOptions = ratingPeriods.filter(
            (r: IRatingPeriod) =>
              new Date(r.minDate) > new Date(currentRatingPeriod.minDate) &&
              new Date(r.minDate) <= dateWithoutTime &&
              r.id != this.data.ratingPeriodId
          );
          this.existingClasses = result;
          this.displayResult = true;
        }
      });
  }

  initializeForms(): void {
    this.reEnrollmentForm = this.fb.group({
      ratingPeriodId: [null, Validators.required],
      ageGradeId: [null],
    });
  }

  handleRatingPeriod(ratingPeriod: any): void {
    ratingPeriod = this.ratingOptions.find(rp => rp.id == ratingPeriod.value);

    this.selectedSchoolYearId = ratingPeriod.schoolYearId ?? null;
    this.is2025StateConfig = ratingPeriod.schoolYear.enforceStateConfig && this.data.ratingViewVersionId == RatingViewVersionEnum.VERSION_2025;
    this.requireAgeGrade = this.is2025StateConfig || this.data.isSoftImplementation;
    this.ageGradeId.setValidators(this.requireAgeGrade ? [Validators.required] : []);
    this.ageGradeId.updateValueAndValidity();
  }

  handleAgeGrade(ageGrade: any): void {
    this.ageGradeId.setValue(ageGrade?.id);
  }

  submit(): void {
    if (this.reEnrollmentForm.invalid) return;

    if (this.ratingPeriodId.value < this.data.ratingPeriodId) {
      this.toastService.error(
        this.translocoService.translate(
          'user.manageEnrollment.reEnrollClassroom.futureRatingPeriodsOnly'
        )
      );
      return;
    }

    if (this.isUserState2025Enforced && !this.is2025StateConfig) {
      this.toastService.error(this.translocoService.translate('shared.ratingPeriods.ratingViewRejected'));
      return;
    }

    const reEnrollClass = this.existingClasses.filter(
      (ec) => ec.ratingPeriodId == this.ratingPeriodId.value
    )[0];
    const payload: any = {
      fromClassId: this.data.id,
      toClassId: reEnrollClass.id,
      requestReasonId: StudentRequestReason.ReEnroll,
      ageGradeId: this.ageGradeId.value,
    };
    this.isProcessing = true;
    const successMessage = this.isAdmin
      ? this.translocoService.translate(
          'user.manageEnrollment.reEnrollClassroom.reEnrollSuccess'
        )
      : this.translocoService.translate(
          'user.manageEnrollment.reEnrollClassroom.requestSentSuccess'
        );
    this.studentRequestService
      .addStudentRequest(payload, successMessage)
      .pipe(take(1))
      .subscribe((r) => {
        this.isProcessing = false;
        this.dialogRef.close({ success: true });
      });
  }
}
